<template>
  <div class="ebook-seo-content">
    <!-- 服务优势区域 -->
    <section class="advantages-section">
      <div class="container">
        <h2 class="section-title">{{ $t('ebook.advantages.title') }}</h2>
        <div class="advantages-grid">
          <div class="advantage-item" v-for="(line, idx) in advantageLines" :key="idx">
            <Icon name="material-symbols:check-circle" class="advantage-icon" />
            <span>{{ line }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- 格式说明卡片 -->
    <section class="format-cards-section">
      <div class="container">
        <EbookFormatCards />
      </div>
    </section>

    <!-- 适用场景 -->
    <section class="scenarios-section">
      <div class="container">
        <EbookScenarios />
      </div>
    </section>

    <!-- FAQ部分 -->
    <section class="faq-section">
      <div class="container">
        <EbookFAQ />
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
// 导入子组件
import EbookFormatCards from './EbookFormatCards.vue'
import EbookScenarios from './EbookScenarios.vue'
import EbookFAQ from './EbookFAQ.vue'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const advantageLines = computed(() => [
  t('ebook.advantages.items.0'),
  t('ebook.advantages.items.1'),
  t('ebook.advantages.items.2'),
  t('ebook.advantages.items.3')
])
</script>

<style scoped lang="scss">
@use 'sass:color';

.ebook-seo-content {
  background: $white;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 $spacing-lg;
}

// 服务优势区域
.advantages-section {
  padding: $spacing-xxl 0;
  background: linear-gradient(135deg,
    rgba(#4A90E2, 0.03) 0%,
    rgba(#FF8C42, 0.03) 50%,
    rgba(#52C41A, 0.03) 100%);

  .section-title {
    text-align: center;
    font-size: 2.2rem;
    font-weight: 700;
    color: $gray-900;
    margin: 0 0 $spacing-xl 0;

    background: linear-gradient(135deg, #4A90E2 0%, #FF8C42 50%, #52C41A 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .advantages-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: $spacing-lg;
    margin-top: $spacing-xl;
  }

  .advantage-item {
    display: flex;
    align-items: center;
    padding: $spacing-lg;
    background: $white;
    border-radius: $border-radius-lg;
    box-shadow: 0 4px 15px rgba($black, 0.08);
    border: 1px solid rgba(#4A90E2, 0.1);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 4px;
      background: linear-gradient(135deg, #4A90E2 0%, #FF8C42 50%, #52C41A 100%);
    }

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 8px 25px rgba(#4A90E2, 0.15);
      border-color: rgba(#4A90E2, 0.2);

      .advantage-icon {
        transform: scale(1.2);
        color: #FF8C42;
      }
    }

    .advantage-icon {
      color: #52C41A;
      font-size: 1.5rem;
      margin-right: $spacing-md;
      flex-shrink: 0;
      transition: all 0.3s ease;
      filter: drop-shadow(0 2px 4px rgba(#52C41A, 0.2));
    }

    span {
      font-size: 1rem;
      color: $gray-800;
      font-weight: 500;
      line-height: 1.4;
    }
  }
}

// 各个section的间距调整
.format-cards-section {
  .container {
    padding: 0 $spacing-lg;
  }
}

.scenarios-section {
  .container {
    padding: 0 $spacing-lg;
  }
}

.faq-section {
  .container {
    padding: 0 $spacing-lg;
  }
}

// 响应式设计
@media (max-width: 768px) {
  .container {
    padding: 0 $spacing-md;
  }

  .advantages-section {
    padding: $spacing-xl 0;

    .section-title {
      font-size: 1.8rem;
    }

    .advantages-grid {
      grid-template-columns: 1fr;
      gap: $spacing-md;
    }

    .advantage-item {
      padding: $spacing-md;

      .advantage-icon {
        font-size: 1.3rem;
        margin-right: $spacing-sm;
      }

      span {
        font-size: 0.95rem;
      }
    }
  }

  .format-cards-section,
  .scenarios-section,
  .faq-section {
    .container {
      padding: 0 $spacing-md;
    }
  }
}

@media (max-width: 480px) {
  .advantages-section {
    .advantage-item {
      flex-direction: column;
      text-align: center;
      gap: $spacing-sm;

      .advantage-icon {
        margin-right: 0;
        margin-bottom: $spacing-xs;
      }
    }
  }
}

// 优势项目动画
.advantage-item {
  animation: fadeInScale 0.6s ease-out;

  &:nth-child(2) {
    animation-delay: 0.15s;
  }

  &:nth-child(3) {
    animation-delay: 0.3s;
  }

  &:nth-child(4) {
    animation-delay: 0.45s;
  }
}

@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
